//frontend/src/components/SearchManagerRequests/Chat/matrixClient.js

const HOMESERVER =
  process.env.REACT_APP_MATRIX_HOMESERVER ||
  "https://ruschinawiki.duckdns.org";

let _txn = 0;
function txnId() {
  _txn += 1;
  return `${Date.now()}_${_txn}`;
}

function authHeaders(accessToken, extra = {}) {
  return {
    Authorization: `Bearer ${accessToken}`,
    ...extra,
  };
}

async function jsonFetch(url, opts) {
  const res = await fetch(url, opts);
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { raw: text };
  }
  if (!res.ok) {
    const msg =
      (data && (data.error || data.errcode)) ||
      `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return data;
}

// ── Получить историю сообщений ───────────────────────────────
export async function fetchMessages({
  accessToken,
  roomId,
  from = null,
  limit = 50,
}) {
  const qs = new URLSearchParams({ dir: "b", limit: String(limit) });
  if (from) qs.set("from", from);
  const url =
    `${HOMESERVER}/_matrix/client/v3/rooms/${encodeURIComponent(
      roomId
    )}/messages?${qs}`;
  const data = await jsonFetch(url, { headers: authHeaders(accessToken) });
  // Разворачиваем: API отдаёт от новых к старым
  const events = (data.chunk || []).slice().reverse();
  return { events, end: data.end };
}

// ── Отправить текстовое сообщение ────────────────────────────
export async function sendText({ accessToken, roomId, body, replyTo = null }) {
  const content = { msgtype: "m.text", body };

  if (replyTo) {
    content["m.relates_to"] = { "m.in_reply_to": { event_id: replyTo } };
    // fallback по спецификации
    content.body = `> <${replyTo}>\n\n${body}`;
  }

  return sendEvent({
    accessToken,
    roomId,
    type: "m.room.message",
    content,
  });
}

// ── Загрузить файл и отправить как сообщение ─────────────────
export async function uploadAndSendFile({
  accessToken,
  roomId,
  file,
  msgtype, // "m.image" | "m.file" | "m.audio" | "m.video"
  extraInfo = {},
}) {
  const uploadUrl = `${HOMESERVER}/_matrix/media/v3/upload?filename=${encodeURIComponent(
    file.name || "file"
  )}`;
  const up = await fetch(uploadUrl, {
    method: "POST",
    headers: authHeaders(accessToken, {
      "Content-Type": file.type || "application/octet-stream",
    }),
    body: file,
  });
  if (!up.ok) throw new Error(`upload failed HTTP ${up.status}`);
  const { content_uri } = await up.json();

  const content = {
    msgtype,
    body: file.name || "file",
    url: content_uri,
    info: {
      mimetype: file.type,
      size: file.size,
      ...extraInfo,
    },
  };

  return sendEvent({
    accessToken,
    roomId,
    type: "m.room.message",
    content,
  });
}

// ── Реакция на сообщение ─────────────────────────────────────
export async function sendReaction({ accessToken, roomId, eventId, emoji }) {
  return sendEvent({
    accessToken,
    roomId,
    type: "m.reaction",
    content: {
      "m.relates_to": {
        rel_type: "m.annotation",
        event_id: eventId,
        key: emoji,
      },
    },
  });
}

// ── Низкоуровневая отправка события ──────────────────────────
async function sendEvent({ accessToken, roomId, type, content }) {
  const url = `${HOMESERVER}/_matrix/client/v3/rooms/${encodeURIComponent(
    roomId
  )}/send/${encodeURIComponent(type)}/${txnId()}`;
  return jsonFetch(url, {
    method: "PUT",
    headers: authHeaders(accessToken, {
      "Content-Type": "application/json",
    }),
    body: JSON.stringify(content),
  });
}

// ── Профиль пользователя (displayname) ───────────────────────
export async function getProfile({ accessToken, userId }) {
  const url = `${HOMESERVER}/_matrix/client/v3/profile/${encodeURIComponent(
    userId
  )}`;
  try {
    return await jsonFetch(url, { headers: authHeaders(accessToken) });
  } catch {
    return {};
  }
}

// ── Участники комнаты ────────────────────────────────────────
export async function getJoinedMembers({ accessToken, roomId }) {
  const url = `${HOMESERVER}/_matrix/client/v3/rooms/${encodeURIComponent(
    roomId
  )}/joined_members`;
  try {
    const data = await jsonFetch(url, {
      headers: authHeaders(accessToken),
    });
    return data.joined || {};
  } catch {
    return {};
  }
}

export { HOMESERVER };


export async function syncMessages({
  accessToken,
  roomId,
  since = null,
  timeout = 30000,
  signal = null,
}) {
  const filter = JSON.stringify({
    room: {
      rooms: [roomId],
      timeline: { limit: 50 },
      state: { lazy_load_members: true },
      ephemeral: { types: [] },
      account_data: { types: [] },
    },
    presence: { types: [] },
    account_data: { types: [] },
  });

  const qs = new URLSearchParams({
    filter,
    timeout: String(timeout),
  });
  if (since) qs.set("since", since);

  const url = `${HOMESERVER}/_matrix/client/v3/sync?${qs}`;
  const data = await jsonFetch(url, {
    headers: authHeaders(accessToken),
    signal, // AbortController → чтобы отменить при закрытии виджета
  });

  const room = data.rooms?.join?.[roomId];
  const events = room?.timeline?.events || [];

  return {
    events,
    nextBatch: data.next_batch,
  };
}